import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import clsx from "clsx";
import { FlaskConical } from "lucide-react";
import { api, request, type S } from "@/shared/api/client";
import { errorMessage } from "@/shared/i18n";
import { useCan, useMe } from "@/shared/lib/session";

const demoKey = ["/v1/core/demo"];
const wait = (ms: number) => new Promise((r) => setTimeout(r, ms));

/**
 * The demo company: a workspace of its own next to this one, made the first time it's
 * opened (it fills in the background, in seconds) and entered on its own address
 * without signing in again.
 */
export function useDemo() {
  const { t } = useTranslation();
  const can = useCan();
  const { data: me } = useMe();
  const qc = useQueryClient();
  const [opening, setOpening] = useState(false);
  const enabled = can("core.apps.manage") && !me?.tenant.demo_back_url;
  const q = useQuery({ queryKey: demoKey, queryFn: () => api.get<S["DemoStatus"]>("/v1/core/demo"), enabled });
  /** Resolves true once the browser is on its way to the demo. */
  const open = async (role = "owner") => {
    setOpening(true);
    try {
      let { state } = await api.post<S["DemoStatus"]>("/v1/core/demo");
      while (state === "preparing") {
        await wait(1500);
        ({ state } = await api.get<S["DemoStatus"]>("/v1/core/demo"));
      }
      if (state === "ready") {
        const { url } = await request<S["DemoLink"]>("POST", "/v1/core/demo/open", { body: {}, query: { as: role } });
        window.location.assign(url); // stays "opening" until the demo's page loads
        return true;
      }
      toast.error(t("demo.failed"));
    } catch (e) {
      toast.error(errorMessage(e));
    }
    setOpening(false);
    await qc.invalidateQueries({ queryKey: demoKey });
    return false;
  };
  const remove = async () => {
    await request("DELETE", "/v1/core/demo");
    await qc.invalidateQueries({ queryKey: demoKey });
  };
  return { enabled, state: q.data?.state, opening, open, remove };
}

/** Opens the demo company (as role), making it first when there's none. */
export function DemoButton({ className, label, role }: { className?: string; label?: string; role?: string }) {
  const { t } = useTranslation();
  const demo = useDemo();
  if (!demo.enabled) return null;
  return (
    <button type="button" className={clsx("btn-ghost", className)} disabled={demo.opening} onClick={() => demo.open(role)}>
      <FlaskConical className="size-4" /> {demo.opening ? t("demo.preparing") : (label ?? t("demo.open"))}
    </button>
  );
}
